import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { Icon } from "@/components/ui/display/Icon";
import type { ChipGroupProps, ChipGroupItem } from "./types";
import styles from "./ChipGroup.module.css";

/**
 * ChipGroup component - Displays a group of chips with overflow indicator.
 * Shows a limited number of chips with "+N" for remaining items.
 * Click to expand vertically showing all items with their labels.
 * Items can be removed individually from the expanded panel.
 */
export const ChipGroup = React.forwardRef<HTMLDivElement, ChipGroupProps>(
  (
    {
      items,
      max = 3,
      size = "md",
      expandable = true,
      panelTitle,
      onRemove,
      onChipClick,
      onOverflowClick,
      className,
      "data-testid": testId,
    },
    ref,
  ) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Ensure max is at least 1
    const effectiveMax = Math.max(1, max);

    // Split items into visible and hidden
    const visibleItems = items.slice(0, effectiveMax);
    const hiddenItems = items.slice(effectiveMax);
    const overflowCount = hiddenItems.length;

    // Close when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsExpanded(false);
        }
      };

      if (isExpanded) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isExpanded]);

    // Close on escape key
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setIsExpanded(false);
        }
      };

      if (isExpanded) {
        document.addEventListener("keydown", handleEscape);
      }

      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }, [isExpanded]);

    const handleGroupClick = () => {
      if (expandable) {
        setIsExpanded(!isExpanded);
      }
      if (onOverflowClick && overflowCount > 0) {
        onOverflowClick(hiddenItems);
      }
    };

    const handleClose = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsExpanded(false);
    };

    const handleItemClick = (item: ChipGroupItem) => {
      if (onChipClick) {
        onChipClick(item);
      }
    };

    const handleRemoveItem = (e: React.MouseEvent, item: ChipGroupItem) => {
      e.stopPropagation();
      if (onRemove) {
        onRemove(item);
      }
    };

    // Don't render if no items
    if (items.length === 0) {
      return null;
    }

    return (
      <div
        ref={containerRef}
        className={classNames(styles.container, styles[size], className)}
      >
        {/* Collapsed group - always visible */}
        <div
          ref={ref}
          className={classNames(styles.group, {
            [styles.clickable]: expandable,
          })}
          onClick={handleGroupClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleGroupClick();
            }
          }}
          role={expandable ? "button" : undefined}
          tabIndex={expandable ? 0 : undefined}
          aria-expanded={expandable ? isExpanded : undefined}
          data-testid={testId}
        >
          {visibleItems.map((item) => (
            <div
              key={item.id}
              className={styles.chip}
              data-testid={testId ? `${testId}-chip-${item.id}` : undefined}
            >
              <span className={styles.chipLabel}>{item.label}</span>
            </div>
          ))}

          {overflowCount > 0 && (
            <div
              className={classNames(styles.chip, styles.overflow)}
              data-testid={testId ? `${testId}-overflow` : undefined}
            >
              +{overflowCount}
            </div>
          )}
        </div>

        {/* Expanded panel overlay - floats above without affecting layout */}
        {isExpanded && expandable && (
          <div className={styles.expandedContainer}>
            {/* Header row: optional title + close button */}
            <div className={styles.headerRow}>
              {panelTitle && (
                <span className={styles.panelTitle}>{panelTitle}</span>
              )}
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close"
              >
                <Icon name="close" />
              </button>
            </div>

            {/* All items */}
            <div className={styles.expandedList}>
              {items.map((item) => (
                <div
                  key={item.id}
                  className={styles.expandedItem}
                  onClick={() => handleItemClick(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleItemClick(item);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span className={styles.expandedItemLabel}>{item.label}</span>
                  {item.removable !== false && onRemove && (
                    <button
                      type="button"
                      className={styles.expandedItemRemove}
                      onClick={(e) => handleRemoveItem(e, item)}
                      aria-label={`Remove ${item.label}`}
                    >
                      <Icon name="x" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  },
);

ChipGroup.displayName = "ChipGroup";

export default ChipGroup;
